/* eslint-disable max-len */
import { AxiosError } from 'axios';
import { ResponsiveLine } from '@nivo/line';
import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useTheme } from 'styled-components';

import { FaHashtag } from 'react-icons/fa';

import AsyncTable, { Column, RequestPage } from '../../../components/AsyncTable';
import Btn from '../../../components/Buttons/Btn';
import CardIcon from '../../../components/CardIcon';
import {
  backendErrorNotification,
  frontendNotification,
} from '../../../components/Notification';
import { NotificationTypes } from '../../../components/Notification/notificationEnums';
import SelectAsyncJSX from '../../../components/SelectAsync';
import SimpleSelect from '../../../components/SimpleSelect';

import HttpStatus from '../../../enums/httpStatus';
import { UrlPaths } from '../../../enums/urlPaths.enum';

import history from '../../../services/history';
import { reportsForSuperadminApi, tenantsReq } from '../../../services/requests';
import { StoreState } from '../../../store/createStore';

import ContentContainer from '../../../templates/Content/ContentContainer';
import { ApiResPaginated } from '../../../types/apiResponseTypes';
import { MetricsTenantReport } from '../../../types/apiResponse/reportsForSuperadmin';
import { SelectOption } from '../../../utils/getSelectOptions';
import { Tenant } from '../Tenant/types';

type ReportType = 'sales' | 'access';

type Props = {
  type: ReportType;
  pageTitle: string;
};

type ReportChartItem = {
  date: string;
  dateLabel: string;
  total: number;
  integrated: number;
  accesses: number;
  synchronizations: number;
};

type ChartMetricKey = 'total' | 'integrated' | 'accesses' | 'synchronizations';

type ReportChartLegend = {
  id: ChartMetricKey;
  label: string;
  color: string;
};

const AGGREGATED_ENV_FILTER = 'AGGREGATED';

const getCurrentDate = (days: number = 0, op: 'add' | 'sub' = 'add'): string => {
  const today = new Date();
  if (op === 'add') {
    today.setDate(today.getDate() + days);
  } else {
    today.setDate(today.getDate() - days);
  }
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatCurrency = (value: number): string =>
  value.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
  });

const formatDateLabel = (date: string): string => {
  const [year, month, day] = date.split('-');
  return year && month && day ? `${day}/${month}` : date;
};

const ReportsDashboard = ({ type, pageTitle }: Props) => {
  const { user } = useSelector((state: StoreState) => state.auth);
  const { t } = useTranslation();
  const theme = useTheme();

  const [reports, setReports] = useState<MetricsTenantReport[]>([]);
  const [updateTable, setUpdateTable] = useState(0);

  const [tenantSelected, setTenantSelected] = useState<SelectOption | null>(null);
  const [smartsellEnvSelected, setSmartsellEnvSelected] = useState(AGGREGATED_ENV_FILTER);
  const [initialDate, setInitialDate] = useState<string>(getCurrentDate(7, 'sub'));
  const [finalDate, setFinalDate] = useState<string>(getCurrentDate());

  const [totals, setTotals] = useState({
    qtd_orders: 0,
    qtd_orders_integrated: 0,
    total_sales_value: 0,
    total_sales_value_integrated: 0,
    qtd_access: 0,
    qtd_sync: 0,
  });

  const chartLegends = useMemo<ReportChartLegend[]>(() => (
    type === 'sales'
      ? [
        { id: 'total', label: t('totalSalesValue') as string, color: '#28a745' },
        { id: 'integrated', label: `${t('integrated')} (${t('value')})`, color: '#DE7223' },
      ]
      : [
        { id: 'accesses', label: t('accesses') as string, color: '#1D98DF' },
        { id: 'synchronizations', label: t('synchronizations') as string, color: '#6c63ff' },
      ]
  ), [type, t]);

  const aggregatedChartData = useMemo(() => {
    const rowsByDate = new Map<string, ReportChartItem>();

    reports.forEach((report) => {
      const current = rowsByDate.get(report.date) || {
        date: report.date,
        dateLabel: formatDateLabel(report.date),
        total: 0,
        integrated: 0,
        accesses: 0,
        synchronizations: 0,
      };

      if (type === 'sales') {
        current.total += Number(report.total_sales_value || 0);
        current.integrated += Number(report.total_sales_value_integrated || 0);
      } else {
        current.accesses += report.qtd_access || 0;
        current.synchronizations += report.qtd_sync || 0;
      }

      rowsByDate.set(report.date, current);
    });

    return Array.from(rowsByDate.values()).sort((a, b) => a.date.localeCompare(b.date));
  }, [reports, type]);

  const chartData = useMemo(() => chartLegends.map((legend) => ({
    id: legend.label,
    data: aggregatedChartData.map((item) => ({
      x: item.dateLabel,
      y: item[legend.id],
    })),
  })), [aggregatedChartData, chartLegends]);

  const formatChartValue = (value: number) => (
    type === 'sales' ? formatCurrency(value) : value.toLocaleString('pt-BR')
  );

  useEffect(() => {
    if (!user?.is_superadmin) {
      frontendNotification({
        message: 'Área restrita!',
        type: NotificationTypes.WARNING,
      });
      history.push(UrlPaths.LOGIN);
    }
  }, [user]);

  const sumTotals = (rows: MetricsTenantReport[]) =>
    rows.reduce(
      (acc, row) => ({
        qtd_orders: acc.qtd_orders + (row.qtd_orders || 0),
        qtd_orders_integrated: acc.qtd_orders_integrated + (row.qtd_orders_integrated || 0),
        total_sales_value: acc.total_sales_value + Number(row.total_sales_value || 0),
        total_sales_value_integrated:
          acc.total_sales_value_integrated + Number(row.total_sales_value_integrated || 0),
        qtd_access: acc.qtd_access + (row.qtd_access || 0),
        qtd_sync: acc.qtd_sync + (row.qtd_sync || 0),
      }),
      {
        qtd_orders: 0,
        qtd_orders_integrated: 0,
        total_sales_value: 0,
        total_sales_value_integrated: 0,
        qtd_access: 0,
        qtd_sync: 0,
      },
    );

  const getItems: RequestPage = async ({ page, quantityPerPage }) => {
    let hasMore = true;
    const source = reportsForSuperadminApi.axios.CancelToken.source();

    try {
      let query = `?page=${page}&quantityPerPage=${quantityPerPage}&type=${type}`;
      query += `&initial_date=${initialDate}&final_date=${finalDate}`;
      if (tenantSelected) {
        query += `&tenant_id=${tenantSelected.value}`;
      }
      if (type === 'access' && smartsellEnvSelected !== AGGREGATED_ENV_FILTER) {
        query += `&smartsell_env=${smartsellEnvSelected}`;
      }
      reportsForSuperadminApi.query = query;

      const res = await reportsForSuperadminApi.index(source.token);

      if (res.status !== HttpStatus.OK) {
        throw res;
      }

      const { data, paginated }: ApiResPaginated<MetricsTenantReport[]> = res.data;

      if (data.length === 0) {
        hasMore = false;
      }

      setTotals(sumTotals(data));

      return {
        data,
        hasMore,
        totalPage: paginated.totalPage,
      };
    } catch (err) {
      if (!reportsForSuperadminApi.axios.isCancel(err)) {
        backendErrorNotification(err as AxiosError<any, any>);
      }

      return {
        data: [],
        hasMore: false,
        totalPage: 0,
      };
    }
  };

  const handleFilterBtn = () => {
    setUpdateTable(updateTable + 1);
  };

  const smartsellEnvOptions = [
    { value: AGGREGATED_ENV_FILTER, label: t('aggregated') },
    { value: 'SMARTSELL_WEB', label: t('SMARTSELL_WEB') },
    { value: 'SMARTSELL_MOBILE', label: t('SMARTSELL_MOBILE') },
    { value: 'SMARTSELL_SALES', label: t('SMARTSELL_SALES') },
    { value: 'FASTMANAGER', label: t('FASTMANAGER') },
    { value: 'EXTERNAL', label: t('EXTERNAL') },
  ];

  const salesColumns: Column<MetricsTenantReport>[] = [
    { label: t('date') as string, accessor: 'date' },
    {
      label: t('tenant') as string,
      Cell: (v) => (v.tenant ? `${v.tenant.id} - ${v.tenant.name}` : '-'),
    },
    {
      label: t('origin') as string,
      Cell: (v) => v.orderOrigin?.label || v.orderOrigin?.code || '-',
    },
    { label: t('quantityOfOrders') as string, accessor: 'qtd_orders' },
    { label: t('integrated') as string, accessor: 'qtd_orders_integrated' },
    {
      label: t('totalSalesValue') as string,
      Cell: (v) => formatCurrency(Number(v.total_sales_value || 0)),
    },
    {
      label: `${t('integrated')} (${t('value')})`,
      Cell: (v) => formatCurrency(Number(v.total_sales_value_integrated || 0)),
    },
  ];

  const accessColumns: Column<MetricsTenantReport>[] = [
    { label: t('date') as string, accessor: 'date' },
    {
      label: t('tenant') as string,
      Cell: (v) => (v.tenant ? `${v.tenant.id} - ${v.tenant.name}` : '-'),
    },
    {
      label: t('environment') as string,
      Cell: (v) => v.smartsellEnvironment?.label || v.smartsell_env || t('aggregated'),
    },
    { label: t('accesses') as string, accessor: 'qtd_access' },
    { label: t('synchronizations') as string, accessor: 'qtd_sync' },
  ];

  const columns = type === 'sales' ? salesColumns : accessColumns;

  const renderCards = () => {
    if (type === 'sales') {
      return (
        <>
          <CardIcon
            color="#6c63ff"
            icon={<FaHashtag />}
            title={t('quantityOfOrders')}
            text={String(totals.qtd_orders)}
          />
          <CardIcon
            color="#1D98DF"
            icon={<FaHashtag />}
            title={`${t('quantityOfOrders')} (${t('integrated')})`}
            text={String(totals.qtd_orders_integrated)}
          />
          <CardIcon
            color="#28a745"
            icon={<FaHashtag />}
            title={t('totalSalesValue')}
            text={formatCurrency(totals.total_sales_value)}
          />
          <CardIcon
            color="#DE7223"
            icon={<FaHashtag />}
            title={`${t('totalSalesValue')} (${t('integrated')})`}
            text={formatCurrency(totals.total_sales_value_integrated)}
          />
        </>
      );
    }

    return (
      <>
        <CardIcon
          color="#1D98DF"
          icon={<FaHashtag />}
          title={t('accesses')}
          text={String(totals.qtd_access)}
        />
        <CardIcon
          color="#6c63ff"
          icon={<FaHashtag />}
          title={t('synchronizations')}
          text={String(totals.qtd_sync)}
        />
      </>
    );
  };

  const renderChart = () => {
    if (!chartData.length) {
      return null;
    }

    return (
      <div
        style={{
          width: '100%',
          height: '360px',
          margin: '1rem 0',
          padding: '1rem',
          backgroundColor: theme.colors.surface,
          borderRadius: '8px',
        }}
      >
        <div
          style={{
            color: theme.colors.onSurface,
            fontWeight: 600,
            marginBottom: '0.5rem',
          }}
        >
          {type === 'sales' ? t('totalSalesValue') : t('accesses')}
        </div>
        <div style={{ height: 'calc(100% - 1.8rem)' }}>
          <ResponsiveLine
            data={chartData}
            margin={{ top: 35, right: 20, bottom: 50, left: type === 'sales' ? 105 : 70 }}
            animate
            enableSlices="x"
            xScale={{ type: 'point' }}
            yScale={{
              type: 'linear',
              min: 'auto',
              max: 'auto',
              stacked: false,
              reverse: false,
            }}
            yFormat={(value) => formatChartValue(Number(value))}
            axisTop={null}
            axisRight={null}
            axisBottom={{
              tickSize: 5,
              tickPadding: 5,
              tickRotation: -35,
            }}
            axisLeft={{
              tickSize: 5,
              tickPadding: 5,
              tickRotation: 0,
              format: (value) => formatChartValue(Number(value)),
            }}
            pointSize={6}
            pointColor={{ theme: 'background' }}
            pointBorderWidth={2}
            pointBorderColor={{ from: 'serieColor' }}
            pointLabelYOffset={0}
            enableCrosshair
            layers={[
              'grid',
              'markers',
              'areas',
              'lines',
              'slices',
              'axes',
              'points',
              'legends',
              'mesh',
              'crosshair',
            ]}
            useMesh
            colors={chartLegends.map((legend) => legend.color)}
            sliceTooltip={({ slice }: any) => {
              return (
                <div
                  style={{
                    padding: '0.5rem 0.75rem',
                    backgroundColor: theme.colors.background,
                    color: theme.colors.onBackground,
                    borderRadius: '5px',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.25)',
                  }}
                >
                  <div style={{ fontWeight: 600, marginBottom: '0.35rem' }}>
                    {String(slice.points[0]?.data.xFormatted || slice.points[0]?.data.x || '')}
                  </div>

                  {slice.points.map((point: any) => (
                    <div
                      key={point.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                      }}
                    >
                      <div
                        style={{
                          width: '10px',
                          height: '10px',
                          backgroundColor: point.serieColor,
                        }}
                      />
                      <span>
                        {`${String(point.serieId)}: ${String(point.data.yFormatted || formatChartValue(Number(point.data.y)))}`}
                      </span>
                    </div>
                  ))}
                </div>
              );
            }}
            legends={[
              {
                anchor: 'top-left',
                direction: 'row',
                justify: false,
                translateX: 0,
                translateY: -30,
                itemWidth: 170,
                itemHeight: 16,
                itemsSpacing: 0,
                symbolSize: 12,
                symbolShape: 'circle',
                itemDirection: 'left-to-right',
                itemTextColor: theme.colors.onSurface,
                data: chartLegends,
              },
            ]}
            theme={{
              textColor: theme.colors.onSurface,
              grid: {
                line: {
                  stroke: theme.colors.onSurface + '30',
                  strokeWidth: 0.5,
                },
              },
              crosshair: {
                line: {
                  stroke: theme.colors.onSurface,
                  strokeWidth: 1,
                  strokeOpacity: 0.75,
                  strokeDasharray: '6 6',
                },
              },
              tooltip: {
                container: {
                  background: theme.colors.surface,
                  color: theme.colors.onSurface,
                },
              },
            }}
          />
        </div>
      </div>
    );
  };

  return (
    <ContentContainer title={pageTitle}>
      <div className="row">
        <div className="inputWithLabel">
          <div className="label">{t('tenant') as string}</div>
          <SelectAsyncJSX
            value={tenantSelected}
            onChange={setTenantSelected}
            request={tenantsReq}
            reqResponseToOption={{
              mapper: {
                label: (item: Tenant) => `${item.id} - ${item.name}`,
                value: (item: Tenant) => item.id,
              },
            }}
            placeholder={t('allTenants')}
          />
        </div>

        {type === 'access' && (
          <div className="inputWithLabel" style={{ maxWidth: '240px' }}>
            <div className="label">{t('environment') as string}</div>
            <SimpleSelect
              options={smartsellEnvOptions}
              value={smartsellEnvSelected}
              onChange={(e: any) => setSmartsellEnvSelected(e.target.value)}
              placeholder={t('select') as string}
            />
          </div>
        )}

        <div className="inputWithLabel" style={{ maxWidth: '200px' }}>
          <div className="label">{t('startDate') as string}</div>
          <input
            className="input"
            type="date"
            value={initialDate}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setInitialDate(e.target.value)}
          />
        </div>

        <div className="inputWithLabel" style={{ maxWidth: '200px' }}>
          <div className="label">{t('endDate') as string}</div>
          <input
            className="input"
            type="date"
            value={finalDate}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setFinalDate(e.target.value)}
          />
        </div>

        <div className="inputWithLabel" style={{ maxWidth: 'max-content', marginRight: 0 }}>
          <div className="label">{''}</div>
          <Btn text={t('filter')} onClick={handleFilterBtn} style={{ marginRight: 0 }} />
        </div>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap' }}>{renderCards()}</div>

      {renderChart()}

      <AsyncTable
        tableName={pageTitle}
        columns={columns}
        value={reports}
        onChange={setReports}
        requestPage={getItems}
        reqListener={[updateTable]}
        options={{
          styles: {
            primaryColor: `${theme.colors.surface}`,
            secondaryColor: `${theme.colors.onSurface}`,
            alternateRowColor: theme.colors.onSurfaceLight,
            textColor: theme.colors.text,
          },
          quantityPerPageLabel: t('quantityPerPage'),
          searchPlaceholder: t('search'),
          totalPageLabel: `${t('totalPages')}: `,
        }}
      />
    </ContentContainer>
  );
};

export default ReportsDashboard;
